import { WarrantyValidationStatusCategory, WarrantyValidationDialogComponent } from './../../../dialogs/warranty-validation-dialog/warranty-validation-dialog.component';
import { RescoringDialogComponent } from './../../../dialogs/rescoring-dialog/rescoring-dialog.component';
import { Authorization } from './../../../models/authorization.model';
import { WarrantyContract } from 'src/app/models/contract.model';
import { DocumentsService } from './../../../services/documents.service';
import { CustomForm, CustomFormField } from 'src/app/models/custom-form.model';
import { MatDialog } from '@angular/material/dialog';
import { GuarantiesService } from './../../../services/guaranties.service';
import { getWarrantyEmail, Warranty } from 'src/app/models/warranty.model';
import { Component, NgZone, OnDestroy, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { Subscription, fromEvent, interval, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UtilsService } from '../../../services/utils.service';
import { ErrorResponse } from '../../../models/error.model';
import { ActivatedRoute } from '@angular/router';
import { ID } from '../../../models/ID.model';
import { generateQuotationCategories } from '../../../models/quote.model';
import { generateOwnerCategoriesV2, getOwnerName } from '../../../models/owner.model';
import { generatePropertyCategories } from 'src/app/models/property.model';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { CategoryV2 } from '../../generics/data-printer-v2/data-printer-v2.component';
import { generateOfficeCategories } from 'src/app/models/office.model';
import { Broker, generateBrokerCategories } from '../../../models/broker.model';
import { Report } from 'src/app/models/report.model';
import { generateTenantCategoriesV2, getTenantName } from 'src/app/models/tenant.model';
import { FormsService } from 'src/app/services/forms.service';
import * as moment from 'moment';
import { PropertySelectorDialogComponent } from '../../../dialogs/property-selector-dialog/property-selector-dialog.component';
import { Property } from '../../../models/property.model';
import { BrokerSelectorDialogComponent } from 'src/app/dialogs/broker-selector-dialog/broker-selector-dialog.component';
import { RequestDocumentationDialogComponent } from 'src/app/dialogs/request-documentation-dialog/request-documentation-dialog.component';
import { Payments } from 'src/app/models/payments.model';
import { PaymentService } from 'src/app/services/payment.service';
import { GenerateContractDialogComponent } from 'src/app/dialogs/generate-contract-dialog/generate-contract-dialog.component';
import { Province } from 'src/app/models/locality.model';
import { SelectOptionsService } from 'src/app/services/select-options.service';
import { ReportsService } from 'src/app/services/reports.service';
import { RequotationDialogComponent } from 'src/app/dialogs/requotation-dialog/requotation-dialog.component';
import { UntypedFormGroup, UntypedFormControl, AbstractControl, UntypedFormArray, Validators } from '@angular/forms';
import { generatePhysicalPersonFG } from 'src/app/models/physical-person.model';
import { generateLegalPersonFG } from 'src/app/models/legal-person.model';
import { generateAddressFG } from 'src/app/models/address.model';

// Mientras el último intento de pago esté en LINK_GENERADO, se refresca la garantía a este
// intervalo para reflejar la confirmación automática del webhook de Mercado Pago sin recarga manual.
// No tiene timeout: el inquilino puede pagar horas después de que el operador abrió la pantalla.
const PAYMENT_POLLING_INTERVAL_MS = 10000;

@Component({
    selector: 'app-warranty-details',
    templateUrl: './warranty-details.component.html',
    styleUrls: ['./warranty-details.component.scss']
})
export class WarrantyDetailsComponent implements OnInit, OnDestroy {

    generateOwnerCategories = generateOwnerCategoriesV2;
    generatePropertyCategories = generatePropertyCategories;
    generateQuotationCategories = generateQuotationCategories;
    generateOfficeCategories = generateOfficeCategories;
    generateBrokerCategories = generateBrokerCategories;
    generateTenantCategories = generateTenantCategoriesV2;
    getWarrantyEmail = getWarrantyEmail;

    warranty?: Warranty;
    provinces?: Province[];
    canEdit = false;

    // Cacheado en vez de invocarse directo desde el template: generatePaymentCategories crea
    // arrays/objetos nuevos en cada llamada, y el template los re-evalúa en cada ciclo de detección
    // de cambios (no solo cuando cambia `warranty`). Como app-data-printer-v2 usa *ngFor sin
    // trackBy, un array "nuevo" en cada ciclo le hacía destruir y recrear el DOM (destello) cada
    // vez que el polling de pago tocaba `refrescandoPago`, aunque `warranty` no hubiera cambiado.
    paymentCategories: CategoryV2[] = [];

    @ViewChild('copyPaymentLinkButton', { static: true })
    copyPaymentLinkButtonTemplate: TemplateRef<any>;

    private paymentPollingSubscription?: Subscription;

    constructor(
        private dialog: MatDialog,
        private route: ActivatedRoute,
        private guarantiesService: GuarantiesService,
        private authService: AuthService,
        private documentsService: DocumentsService,
        private formsService: FormsService,
        private paymentService: PaymentService,
        private selectOptionsService: SelectOptionsService,
        private reportsService: ReportsService,
        public utilsService: UtilsService,
        private ngZone: NgZone,
    ) { }

    ngOnInit(): void {
        const userType = this.authService.getUser()?.tipo;
        if (userType === 'CORPORATIVO') {
            this.canEdit = true;
        }
        this.route.params.subscribe(params => {
            if (params.id) {
                this.getWarranty(params.id);
            }
        });
        this.getProvinces();
    }

    ngOnDestroy(): void {
        this.paymentPollingSubscription?.unsubscribe();
    }

    getWarranty(id: ID) {
        return new Promise<void>((resolve, reject) => {
            this.guarantiesService.getWarrantyById(id).subscribe({
                next: warranty => {
                    this.warranty = warranty;
                    if (warranty.pagos?.principal) {
                        this.paymentCategories = this.generatePaymentCategories(warranty.pagos);
                    }
                    this.updatePaymentPolling();
                    resolve();
                },
                error: (error: ErrorResponse) => {
                    this.utilsService.alert('Error al obtener datos de la garantía', error.error?.descripcion);
                    resolve();
                },
            });
        });
    }

    // Mientras haya un pago con la última operación en LINK_GENERADO, refresca la garantía sola
    // para reflejar la confirmación automática del webhook de Mercado Pago sin que el operador
    // tenga que hacer click en "Actualizar".
    private updatePaymentPolling() {
        const ultimaOperacion = this.warranty?.pagos?.principal?.operaciones?.[0];
        const debePollear = ultimaOperacion?.estado === 'LINK_GENERADO' && !ultimaOperacion.vencido;

        if (debePollear && !this.paymentPollingSubscription) {
            console.log('Iniciando polling de pago', new Date().toLocaleTimeString());
            // Corre fuera de la zona de Angular: mientras no haya un cambio real que mostrar, la
            // consulta HTTP no dispara detección de cambios (y por lo tanto no genera destello),
            // porque Angular corre un ciclo completo de CD cada vez que una request termina, sin
            // importar si se tocó alguna propiedad o no.
            // Con la pestaña oculta no se consulta; al volver a ella se consulta en el momento,
            // sin esperar al próximo tick.
            this.ngZone.runOutsideAngular(() => {
                this.paymentPollingSubscription = merge(
                    interval(PAYMENT_POLLING_INTERVAL_MS),
                    fromEvent(document, 'visibilitychange'),
                ).pipe(
                    filter(() => !document.hidden),
                ).subscribe(() => this.pollPaymentStatus());
            });
        } else if (!debePollear && this.paymentPollingSubscription) {
            this.paymentPollingSubscription.unsubscribe();
            this.paymentPollingSubscription = undefined;
        }
    }

    // Consulta liviana de estado. Corre fuera de la zona de Angular (ver updatePaymentPolling):
    // solo si el estado realmente cambió volvemos a entrar a la zona a propósito, para hacer el
    // refresco completo de la garantía. Mientras no cambió nada, la vista queda intacta.
    private pollPaymentStatus() {
        console.log('Refrescando opcion de pago', new Date().toLocaleTimeString());
        this.paymentService.getPaymentStatus(this.warranty.hash).subscribe({
            next: estado => {
                const ultimaOperacion = this.warranty?.pagos?.principal?.operaciones?.[0];
                const cambioDeEstado = estado.estadoUltimaOperacion !== ultimaOperacion?.estado || estado.vencido !== !!ultimaOperacion?.vencido;
                if (cambioDeEstado) {
                    this.ngZone.run(() => this.getWarranty(this.warranty.id));
                }
            },
            error: () => { },
        });
    }

    getProvinces() {
        this.selectOptionsService.getProvinces().subscribe(
            provinces => this.provinces = provinces,
            (error: ErrorResponse) => this.utilsService.alert('Error al obtener lista de provincias', error.error?.descripcion)
        );
    }

    //////////////////// CATEGORÍAS ////////////////////

    generateGeneralInformationCategories(warranty: Warranty) {
        const categories: CategoryV2[] = [];

        categories.push({
            items: [
                { label: 'ID', value: warranty.id },
            ]
        }, {
            items: [
                { label: 'Estado', value: warranty.estado, type: 'text' },
            ]
        }, {
            items: [
                { label: 'Fecha de creación', value: warranty.fechaCreacion, type: 'datetime' },
            ]
        }, {
            items: [
                { label: 'Última actualización', value: warranty.fechaActualizacion, type: 'datetime' },
            ]
        });

        return categories;
    }

    generateScoringCategories(report: Report) {
        const categories: CategoryV2[] = [];

        categories.push({
            items: [
                { label: 'Estado', value: report.aprobacionManual ? 'APROBADO MANUALMENTE' : report.dictamen, type: 'text' },
            ]
        }, {
            items: [
                { label: 'Fecha del informe', value: report.fechaCreacion, type: 'datetime' },
            ]
        });

        return categories;
    }

    generatePendingPaymentCategories(payments: Payments) {
        const categories: CategoryV2[] = [];

        categories.push({
            items: [
                { label: 'Estado', value: 'PENDIENTE' },
            ]
        }, {
            items: [
                { label: 'Fecha de solicitud', value: payments.fechaSolicitud, type: 'datetime' },
            ]
        });

        return categories;
    }

    generatePaymentCategories(payments: Payments) {
        const categories: CategoryV2[] = [];

        categories.push({
            labelWidth: '150px',
            maxWidth: '450px',
            name: 'Información general',
            items: [
                { label: 'Estado', value: payments.verificado ? 'APROBADO' : 'PENDIENTE DE VERIFICACIÓN' },
                { label: 'Fecha de solicitud', value: payments.fechaSolicitud, type: 'datetime' },
                { label: 'Fecha de información', value: payments.principal.fechaInformacion, type: 'datetime' },
                { label: 'Fecha de verificación', value: payments.principal.fechaVerificacion, type: 'datetime' },
            ]
        });

        if (payments.opcion) {
            categories[0].items.push(
                { label: 'Cuotas', value: payments.opcion.cuotas },
                { label: 'Monto por cuota', value: payments.opcion.montoPorCuota, type: 'money' },
                { label: 'Monto total', value: payments.opcion.montoTotal, type: 'money' },
            );
        }

        // Se muestra solo el último intento (el historial completo sigue existiendo en base,
        // solo se filtra en la vista): el resto queda vencido y no aporta al operador.
        const ultimaOperacion = payments.principal.operaciones[0];
        categories.push(...(ultimaOperacion ? [ultimaOperacion] : []).map((operation) => {
            const category: CategoryV2 = {
                labelWidth: '170px',
                maxWidth: '450px',
                name: 'Último intento de pago',
                items: [
                    { label: 'Estado', value: operation.vencido ? operation.estado + ' (vencido)' : operation.estado },
                    { label: 'Error', value: operation.error },
                    { label: 'Medio de pago', value: operation.medio.descripcion },
                    { label: 'Proveedor', value: operation.proveedor },
                    { label: 'Monto pagado', value: operation.monto, type: 'money' },
                    operation.urlPago && !operation.vencido
                        ? { label: 'Link de pago', template: this.copyPaymentLinkButtonTemplate, templateParams: { url: operation.urlPago } }
                        : { label: 'Link de pago', value: undefined },
                ]
            };
            return category;
        }));

        return categories;
    }

    generateContractCategories(contract: WarrantyContract) {
        const categories: CategoryV2[] = [];

        categories.push({
            items: [
                { label: 'Fecha de firma', value: contract.fechaFirma, type: 'date' },
            ]
        }, {
            items: [
                { label: 'Ciudad de firma', value: contract.datos?.ciudadFirma, type: 'text' },
            ]
        }, {
            items: [
                { label: 'Inicio de alquiler', value: contract.fechaInicioAlquiler, type: 'date' },
            ]
        }, {
            items: [
                { label: 'Fin de alquiler', value: contract.datos?.fechaFinAlquiler, type: 'date' },
            ]
        }, {
            items: [
                { label: 'Firmado digitalmente', value: contract.contratoGarantia.firmadoDigitalmente ? 'SI' : 'NO' },
            ]
        });

        return categories;
    }

    generateAuthorizationCategories(authorization: Authorization) {
        const categories: CategoryV2[] = [];

        categories.push({
            items: [
                { label: 'Tipo de autorización', value: authorization.autorizador?.tipo },
            ]
        }, {
            items: [
                { label: 'Fecha de solicitud', value: authorization.fechaCreacion, type: 'datetime' },
            ]
        }, {
            items: [
                { label: 'Nombre', value: authorization.autorizador?.nombre },
            ]
        }, {
            items: [
                { label: 'Email', value: authorization.autorizador?.email, type: 'email' },
            ]
        });

        if (authorization.fechaConfirmacion) {
            categories.push({
                items: [
                    { label: 'Fecha de confirmación', value: authorization.fechaConfirmacion, type: 'datetime' },
                ]
            });
        }

        if (authorization.fechaConfirmacion && !authorization.aprobada) {
            categories.push({
                items: [
                    { label: 'Motivo de rechazo', value: authorization.motivo },
                ]
            });
        }

        return categories;
    }

    //////////////////// ACCIONES GENERALES ////////////////////

    deleteWarranty() {
        const dialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
                titulo: '¿Dar de baja la garantía?',
                descripcion: 'Esta acción no se puede deshacer.',
                input: 'Motivo',
                botonCancelar: 'Cancelar',
                botonAceptar: 'Confirmar'
            }
        });
        dialog.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined && result !== null) {
                this.guarantiesService.deleteWarranty(this.warranty.id).subscribe(_ => {
                    this.utilsService.alert('Garantía dada de baja correctamente', `Se ha dado de baja la garantía número ${this.warranty.id} de la plataforma.`);
                    this.getWarranty(this.warranty.id);
                }, (error: ErrorResponse) => this.utilsService.alert('Error al borrar garantía', error.error?.descripcion));
            }
        });
    }

    showAdministrationButton() {
        return this.warranty && ['ACTIVA', 'FINALIZADA'].includes(this.warranty.estado) && this.warranty.cotizacion.servicios.find(service => service.codigo === 'ADMINISTRACION_ALQUILER');
    }

    //////////////////// ACCIONES COTIZACION ////////////////////

    openRequotationDialog() {
        const dialog = this.dialog.open(RequotationDialogComponent, {
            data: this.warranty
        });
        dialog.afterClosed().subscribe(result => {
            if (result) {
                this.warranty = result;
            }
        });
    }

    //////////////////// ACCIONES SCORING ////////////////////

    approveScoring() {
        const dialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
                titulo: '¿Desea aprobar el informe?',
                descripcion: 'Por favor ingrese un motivo de aprobación manual.',
                input: 'Motivo',
                botonCancelar: 'Cancelar',
                botonAceptar: 'Confirmar'
            }
        });
        dialog.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined && result !== null) {
                this.reportsService.manuallyApproveReport(this.warranty.informe.id, result).subscribe(report => {
                    this.warranty.informe = report;
                }, (error: ErrorResponse) => this.utilsService.alert('Error al aprobar informe', error.error?.descripcion));
            }
        });
    }

    openRescoringDialog() {
        const dialog = this.dialog.open(RescoringDialogComponent, {
            data: this.warranty
        });
        dialog.afterClosed().subscribe(result => {
            if (result) {
                this.warranty = result;
            }
        });
    }

    //////////////////// ACCIONES PROPIEDAD ////////////////////

    openSelectPropertyModal() {
        this.dialog.open(PropertySelectorDialogComponent, { data: { idAgente: this.warranty.agente?.id } }).afterClosed().subscribe({
            next: (property: Property | undefined) => {
                if (property) {
                    this.guarantiesService.associateProperty(this.warranty.id, property.id).subscribe(warranty => {
                        this.warranty = warranty;
                    }, (error: ErrorResponse) => this.utilsService.alert('Error al asociar propiedad', error.error?.descripcion));
                }
            }
        });
    }

    //////////////////// ACCIONES AGENTE ////////////////////

    openAssignBrokerModal() {
        this.dialog.open(BrokerSelectorDialogComponent).afterClosed().subscribe({
            next: (broker: Broker | undefined) => {
                if (broker) {
                    this.guarantiesService.assignAgent(this.warranty.id, broker.id).subscribe(warranty => {
                        this.warranty = warranty;
                    }, (error: ErrorResponse) => this.utilsService.alert('Error al asignar agente', error.error?.descripcion));
                }
            }
        });
    }

    //////////////////// ACCIONES DOCUMENTACIÓN ////////////////////

    requestInitialDocumentation() {
        const campos: CustomFormField[] = [
            { nombre: 'DNI frente', tipo: 'UPLOAD' },
            { nombre: 'DNI dorso', tipo: 'UPLOAD' },
        ];
        // No obligatorio: el DNI es documentación de rutina y no debe bloquear el pago en REVISION.
        this.requestDocumentation(campos, 'Por favor complete sus datos.', false);
    }

    openRequestDocumentationModal() {
        this.dialog.open(RequestDocumentationDialogComponent).afterClosed().subscribe({
            next: (response: { campos: CustomFormField[], descripcion?: string } | undefined) => {
                if (response) {
                    this.requestDocumentation(response.campos, response.descripcion, !this.warranty.formularios?.length);
                }
            }
        });
    }

    requestDocumentation(campos: CustomFormField[], descripcion?: string, obligatorio?: boolean) {
        if (!getWarrantyEmail(this.warranty)) return;
        this.formsService.requestForm(
            campos,
            descripcion,
            getWarrantyEmail(this.warranty),
            this.warranty.id,
            this.warranty.tipoProducto,
            'CARGA_DATOS',
            obligatorio ?? false
        ).subscribe(
            form => this.warranty.formularios.push(form),
            (error: ErrorResponse) => this.utilsService.alert('Error al generar formulario', error.error?.descripcion)
        );
    }

    isFormExpired(form: CustomForm) {
        return moment(form.vencimiento).isBefore(moment());
    }

    resendForm(form: CustomForm) {
        this.formsService.resendForm(form.id).subscribe(_ => {
            this.utilsService.alert('Formulario reenviado correctamente', 'Si el inquilino sigue sin recibir el email por favor revise la carpeta de spam o complete el formulario manualmente.');
        }, (error: ErrorResponse) => this.utilsService.alert('Error al reenviar formulario', error.error?.descripcion));
    }

    downloadDocument(documentId: ID) {
        this.documentsService.downloadContract(documentId).subscribe(
            ({ blob, filename }) => {
                const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
                this.utilsService.downloadBlobAsFile(blob, filenameWithoutExt);
            },
            (error: ErrorResponse) => this.utilsService.alert('Error al descargar archivo', error.error?.descripcion)
        );
    }

    /**
     * Espejo de DocumentacionGarantiaValidator.formulariosObligatoriosCompletados: no alcanza con que
     * exista un formulario, porque el de DNI se genera solo al crear la garantía y no es obligatorio.
     */
    allRequiredFormsCompleted() {
        const forms = this.warranty.formularios ?? [];
        return forms.some(form => form.estado === 'CERRADO') && forms.every(form => !form.obligatorio || form.estado !== 'PENDIENTE');
    }

    /**
     * Espejo de DocumentacionGarantiaValidator (backend): con el cobro anticipado el pago ya no exige
     * documentación, por lo que es la generación del contrato la que la exige.
     */
    contractPendingRequirement(): string | undefined {
        if (!this.warranty.pagos?.verificado) {
            return 'Verificación de pago pendiente.';
        }
        if (!this.warranty.propiedad) {
            return 'Debe seleccionar la propiedad antes de generar el contrato.';
        }
        if (!this.warranty.agente) {
            return 'Debe asignar un agente antes de generar el contrato.';
        }
        if (!this.allRequiredFormsCompleted()) {
            return 'Debe tener al menos un formulario de documentación cargado y todos los obligatorios completos antes de generar el contrato.';
        }
        return undefined;
    }

    /**
     * Espejo de SolicitarPagoGarantiaUseCaseImpl.scoringHabilitaPago: APROBADO habilita directo,
     * REVISION y RECHAZADO requieren aprobación manual del informe (para RECHAZADO es la aprobación
     * por excepción) + documentación completa.
     */
    scoringHabilitaPago(): boolean {
        const dictamen = this.warranty.informe?.dictamen;
        if (dictamen === 'APROBADO') {
            return true;
        }
        if (dictamen !== 'REVISION' && dictamen !== 'RECHAZADO') {
            return false;
        }
        return !!this.warranty.informe?.aprobacionManual && !!this.allRequiredFormsCompleted();
    }

    //////////////////// ACCIONES PAGO ////////////////////

    resendPaymentEmail() {
        this.guarantiesService.resendPaymentForm(this.warranty.id).subscribe(_ => {
            this.utilsService.alert('Formulario reenviado correctamente', 'Si el inquilino sigue sin recibir el email por favor revise la carpeta de spam o complete el formulario manualmente.');
        }, (error: ErrorResponse) => this.utilsService.alert('Error al reenviar formulario', error.error?.descripcion));
    }

    verifyPayment() {
        this.paymentService.verifyPayment(this.warranty.id, this.warranty.pagos?.principal?.id).subscribe(
            warranty => {
                this.warranty = warranty;
                if (warranty.pagos?.principal) {
                    this.paymentCategories = this.generatePaymentCategories(warranty.pagos);
                }
                this.updatePaymentPolling();
            },
            (error: ErrorResponse) => this.utilsService.alert('Error al verificar pago', error.error?.descripcion)
        );
    }

    requestPayment() {
        this.guarantiesService.requestPayment(this.warranty.id).subscribe(warranty => {
            this.warranty = warranty;
        }, (error: ErrorResponse) => this.utilsService.alert('Error al solicitar pago', error.error?.descripcion));
    }

    copyPaymentLink(url: string) {
        navigator.clipboard.writeText(url).then(() => {
            this.utilsService.alert('Link copiado', 'El link de pago fue copiado al portapapeles.');
        });
    }

    //////////////////// ACCIONES CONTRATO ////////////////////

    openGenerateContractDialog() {
        // Ejecutar la validación después del ciclo de detección de cambios
        setTimeout(() => {
            const warrantyStatus = this.validateWarrantyStatus();
            if (warrantyStatus.every(category => category.fields.length === 0)) {
                this.dialog.open(GenerateContractDialogComponent, {
                    data: { warranty: this.warranty, provinces: this.provinces },
                    panelClass: 'generate-contract-dialog-container'
                }).afterClosed().subscribe({
                    next: (warranty: Warranty | undefined) => {
                        if (warranty) {
                            this.warranty = warranty;
                        }
                    }
                });
            } else {
                const dialog = this.dialog.open(WarrantyValidationDialogComponent, { data: warrantyStatus });
                dialog.afterClosed().subscribe(update => {
                    if (update) {
                        this.getWarranty(this.warranty.id).then(_ => {
                            this.openGenerateContractDialog();
                        });
                    }
                });
            }
        }, 0);
    }

    validateWarrantyStatus() {
        const warrantyStatus: WarrantyValidationStatusCategory[] = [];

        for (const tenant of this.warranty!.inquilinos) {
            const tenantFG = tenant.tipoPersona === 'FISICA'
                ? generatePhysicalPersonFG('TENANT', !!tenant.personaFisica?.representante)
                : generateLegalPersonFG(!!tenant.personaJuridica?.representante);

            this.utilsService.fillFormWithPartialData(tenantFG, tenant.tipoPersona === 'FISICA' ? tenant.personaFisica : tenant.personaJuridica);

            // Excluir tipoDocumento, dni y pasaporte de la validación automática
            const errors = this.getFormControlErrors(tenantFG, '', ['tipoDocumento', 'dni', 'pasaporte']);

            // Validación manual: debe tener DNI O Pasaporte
            if (tenant.tipoPersona === 'FISICA') {
                const hasDni = tenant.personaFisica?.dni && tenant.personaFisica.dni.trim() !== '';
                const hasPasaporte = tenant.personaFisica?.pasaporte && tenant.personaFisica.pasaporte.trim() !== '';

                if (!hasDni && !hasPasaporte) {
                    errors.push('.personaFisica.documento');
                }
            }

            warrantyStatus.push({
                name: 'Inquilino: ' + getTenantName(tenant),
                fields: errors,
                link: `/inquilinos/${tenant.id}/editar`
            });
        }

        for (const owner of this.warranty!.propiedad?.propietarios ?? []) {
            const ownerFG = owner.tipoPersona === 'FISICA'
                ? generatePhysicalPersonFG('TENANT', !!owner.personaFisica?.representante)
                : generateLegalPersonFG(!!owner.personaJuridica?.representante);

            this.utilsService.fillFormWithPartialData(ownerFG, owner.tipoPersona === 'FISICA' ? owner.personaFisica : owner.personaJuridica);

            // Excluir tipoDocumento, dni y pasaporte de la validación automática
            const errors = this.getFormControlErrors(ownerFG, '', ['tipoDocumento', 'dni', 'pasaporte']);

            // Validación manual: debe tener DNI O Pasaporte
            if (owner.tipoPersona === 'FISICA') {
                const hasDni = owner.personaFisica?.dni && owner.personaFisica.dni.trim() !== '';
                const hasPasaporte = owner.personaFisica?.pasaporte && owner.personaFisica.pasaporte.trim() !== '';

                if (!hasDni && !hasPasaporte) {
                    errors.push('.personaFisica.documento');
                }
            }

            warrantyStatus.push({
                name: 'Propietario: ' + getOwnerName(owner),
                fields: errors,
                link: `/propietarios/${owner.id}/editar`
            });
        }

        if (!this.warranty.propiedad) {
            warrantyStatus.push({ name: 'Propiedad', fields: ['.propiedad'] });
            return this.formatWarrantyStatusFields(warrantyStatus);
        }

        const propertyFG = new UntypedFormGroup({
            cantidadAmbientes: new UntypedFormControl('', [Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')]),
            direccion: generateAddressFG()
        });
        this.utilsService.fillFormWithPartialData(propertyFG, this.warranty.propiedad!);
        warrantyStatus.push({
            name: 'Propiedad',
            fields: this.getFormControlErrors(propertyFG),
            link: `/propiedades/${this.warranty.propiedad.id}/editar`
        });

        return this.formatWarrantyStatusFields(warrantyStatus);
    }

    private formatWarrantyStatusFields(warrantyStatus: WarrantyValidationStatusCategory[]): WarrantyValidationStatusCategory[] {
        for (const status of warrantyStatus) {
            status.fields = status.fields.map(field =>
                field.substring(1).split('.').map(word => this.utilsService.camelCaseToText(word)).join(' -> ')
            );
        }
        return warrantyStatus;
    }

    getFormControlErrors(control: AbstractControl, prefix: string = '', excludeFields: string[] = []): string[] {
        const errors: string[] = [];

        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
            for (const key of Object.keys(control.controls)) {
                // Saltar campos excluidos
                if (excludeFields.includes(key)) {
                    continue;
                }
                errors.push(...this.getFormControlErrors(control.controls[key], prefix + '.' + key, excludeFields));
            }
        } else if (control instanceof UntypedFormControl) {
            if (control.status !== 'VALID') {
                return [prefix];
            }
        }

        return errors;
    }

    uploadSignedContract(event: Event) {
        const files = Array.from((event.target as HTMLInputElement).files);
        if (files?.length) {
            this.documentsService.uploadDocumentAWS(files[0], 'CAPTURA_CONTRATO_FIRMADO', this.warranty.id, this.warranty.tipoProducto, true).then(document => {
                this.warranty.capturasFirmaContrato.push(document);
            }).catch((error: ErrorResponse) => this.utilsService.alert('Error al subir contrato firmado', error.error?.descripcion));
        }
    }

    //////////////////// ACCIONES AUTORIZACIÓN ////////////////////

    resendAuthorizationEmail(authorization: Authorization) {
        this.guarantiesService.resendAuthorizationEmail(authorization.id).subscribe(
            _ => this.utilsService.alert('Authorización reenviada correctamente', 'Estaremos en contacto con usted cuando sea aprobada.'),
            (error: ErrorResponse) => this.utilsService.alert('Error al reenviar email de autorización', error.error?.descripcion)
        );
    }

    reportAuthorization(authorization: Authorization, dictum: 'aprobacion' | 'rechazo') {
        if (dictum === 'rechazo') {
            const dialog = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    titulo: '¿Está seguro que desea rechazar la garantía?',
                    descripcion: 'Por favor ingrese la razón del rechazo.',
                    input: 'Razón',
                    botonAceptar: 'Aceptar',
                    botonCancelar: 'Cancelar'
                }
            });
            dialog.afterClosed().subscribe((reason: string | false) => {
                if (reason !== false) {
                    this.sendAuthorizationReport(authorization, dictum, reason);
                }
            });
        } else {
            this.sendAuthorizationReport(authorization, dictum);
        }
    }

    sendAuthorizationReport(authorization: Authorization, dictum: 'aprobacion' | 'rechazo', reason?: string) {
        this.guarantiesService.reportAuthorization(authorization.id, dictum, '', reason).subscribe(newAuthorization => {
            this.getWarranty(this.warranty.id);
        }, (error: ErrorResponse) => this.utilsService.alert('Error al informar su decisión', error.error?.descripcion));
    }

    //////////////////// ACCIONES FIRMA DIGITAL ////////////////////

    uploadDigitalSignedContract(event: Event) {
        const files = Array.from((event.target as HTMLInputElement).files);
        if (files?.length) {
            if (files[0].name !== this.warranty.contratos?.contratoGarantia?.nombreArchivo) {
                this.utilsService.alert('Error al subir el archivo', `El nombre del contrato firmado debe ser el mismo que el contrato que se descargó de la plataforma:\n${this.warranty.contratos?.contratoGarantia?.nombreArchivo}`);
                return;
            }
            this.documentsService.uploadDocumentAWS(files[0], 'CONTRATO', undefined, undefined, true).then(document => {
                this.documentsService.reportSignedDocuments([document.id]).subscribe(
                    _ => this.getWarranty(this.warranty.id),
                    (error: ErrorResponse) => this.utilsService.alert('Error al subir el archivo ' + files[0].name, error.error?.descripcion)
                );
            }).catch((error: ErrorResponse) => this.utilsService.alert('Error al subir el archivo ' + files[0].name, error.error?.descripcion));
        }
    }

    //////////////////// ACCIONES FIRMA ////////////////////

    readyToSign() {
        const signDate = (this.warranty?.contratos as WarrantyContract | undefined)?.fechaFirma;
        return signDate && moment().isAfter(moment(signDate));
    }

    reportSignature() {
        this.guarantiesService.reportSignature(this.warranty?.id).subscribe(
            warranty => this.warranty = warranty,
            (error: ErrorResponse) => this.utilsService.alert('Error al informar firma', error.error?.descripcion)
        );
    }
}
